import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Typography,
  Badge,
  Calendar,
  Tag,
} from 'antd';
import dayjs from 'dayjs';
import { adminOperationsService } from '../../services/adminService';
import { formatLkr } from '../../utils/currency';
import { PAYMENT_METHOD_OPTIONS } from '../../constants/paymentMethods';
import { hasPermission } from '../../utils/permissions';

type OperationsTabKey =
  | 'billing'
  | 'sports'
  | 'calendar'
  | 'notifications';

interface OperationsSuiteProps {
  defaultTab: OperationsTabKey;
}

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;


// Recording/settling payments requires billing:edit on the backend.
// Module-scope read so useMemo dependency lists stay stable.
const canSettle = (): boolean => hasPermission("billing:edit");

const OperationsSuite: React.FC<OperationsSuiteProps> = ({ defaultTab }) => {
  const canManageSports = hasPermission("activities:edit");
  const [activeTab, setActiveTab] = useState<OperationsTabKey>(defaultTab);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [billingOverview, setBillingOverview] = useState<any>(null);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [billingReferences, setBillingReferences] = useState<{
    bookings: any[];
    orders: any[];
    activities: any[];
    folios: any[];
  }>({ bookings: [], orders: [], activities: [], folios: [] });
  const [selectedRefType, setSelectedRefType] = useState<'booking' | 'order' | 'activity' | 'folio' | null>(null);
  const [selectedRefId, setSelectedRefId] = useState<string | null>(null);
  const [unifiedBillingForm] = Form.useForm();
  const [sportsCatalog, setSportsCatalog] = useState<any[]>([]);
  const [editingSportsCatalog, setEditingSportsCatalog] = useState<any | null>(null);
  const [sportsSchedules, setSportsSchedules] = useState<any[]>([]);
  const [sportsReservations, setSportsReservations] = useState<any[]>([]);
  const [calendarEntries, setCalendarEntries] = useState<any[]>([]);
  const [notifications, setNotifications] = useState<any[]>([]);

  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);

  const [sportsCatalogForm] = Form.useForm();
  const [sportsScheduleForm] = Form.useForm();
  const [notificationForm] = Form.useForm();

  const fetchTabData = useCallback(async (tab: OperationsTabKey) => {
    setLoading(true);
    setError(null);

    try {
      if (tab === 'billing') {
        const [overviewResponse, invoicesResponse, referencesResponse, foliosResponse] = await Promise.all([
          adminOperationsService.getBillingOverview(),
          adminOperationsService.getBillingInvoices(),
          adminOperationsService.getBillingReferences(),
          adminOperationsService.searchFolios(),
        ]);

        setBillingOverview(overviewResponse.data);
        setInvoices(Array.isArray(invoicesResponse.data) ? invoicesResponse.data : []);
        if (referencesResponse?.success && referencesResponse?.data) {
          const resData = referencesResponse.data as any;
          setBillingReferences({
            bookings: Array.isArray(resData?.bookings) ? resData.bookings : [],
            orders: Array.isArray(resData?.orders) ? resData.orders : [],
            activities: Array.isArray(resData?.activities) ? resData.activities : [],
            folios: foliosResponse?.success && foliosResponse?.data
              ? (foliosResponse.data as any).folios || []
              : [],
          });
        }
      }

      if (tab === 'sports') {
        const [catalogResponse, schedulesResponse, reservationsResponse] = await Promise.all([
          adminOperationsService.getSportsCatalog(),
          adminOperationsService.getSportsSchedules(),
          adminOperationsService.getSportsReservations(),
        ]);

        setSportsCatalog(Array.isArray(catalogResponse.data) ? catalogResponse.data : []);
        setSportsSchedules(Array.isArray(schedulesResponse.data) ? schedulesResponse.data : []);
        setSportsReservations(Array.isArray(reservationsResponse.data) ? reservationsResponse.data : []);
      }

      if (tab === 'calendar') {
        const fallbackStart = dayjs().startOf('month').format('YYYY-MM-DD');
        const fallbackEnd = dayjs().endOf('month').format('YYYY-MM-DD');

        const response = await adminOperationsService.getUnifiedCalendar({
          startDate: dateRange ? dateRange[0].format('YYYY-MM-DD') : fallbackStart,
          endDate: dateRange ? dateRange[1].format('YYYY-MM-DD') : fallbackEnd,
        });

        setCalendarEntries(Array.isArray(response.data) ? response.data : []);
      }

      if (tab === 'notifications') {
        const response = await adminOperationsService.getNotifications();
        setNotifications(Array.isArray(response.data) ? response.data : []);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unexpected error';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    void fetchTabData(activeTab);
  }, [activeTab, dateRange, fetchTabData]);

  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);

  const invoiceColumns = useMemo(
    () => [
      { title: 'Invoice #', dataIndex: 'invoice_number', key: 'invoice_number' },
      {
        title: 'Type',
        key: 'type',
        render: (_: any, record: any) => {
          if (record.record_type === 'folio') return <Tag color="cyan">Guest Folio</Tag>;
          if (record.booking_id) return <Tag color="blue">Room Booking</Tag>;
          if (record.order_id) return <Tag color="purple">Restaurant</Tag>;
          if (record.activity_id) return <Tag color="orange">Adam's Tour</Tag>;
          return <Tag color="default">Other</Tag>;
        }
      },
      { title: 'Guest', dataIndex: 'guest_name', key: 'guest_name' },
      {
        title: 'Rooms',
        dataIndex: 'room_count',
        key: 'room_count',
        render: (value: number, record: any) => {
          if (record.record_type === 'order' || record.record_type === 'activity') return '—';
          return value > 1 ? value : '—';
        },
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (status: string) => {
          const colorMap: Record<string, string> = {
            paid: 'green',
            partially_paid: 'orange',
            pending: 'red',
            cancelled: 'default',
          };
          return <Tag color={colorMap[status] || 'default'}>{status?.replace('_', ' ').toUpperCase()}</Tag>;
        },
      },
      {
        title: 'Total',
        dataIndex: 'total_amount',
        key: 'total_amount',
        render: (value: number) => formatLkr(Number(value || 0)),
      },
      {
        title: 'Paid',
        dataIndex: 'paid_amount',
        key: 'paid_amount',
        render: (value: number) => formatLkr(Number(value || 0)),
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_: any, record: any) => {
          let refType: 'booking' | 'order' | 'activity' | 'folio' = 'booking';
          let refId = '';

          if (record.record_type === 'folio') {
            refType = 'folio';
            refId = record.id;
          } else if (record.booking_id) {
            refType = 'booking';
            refId = record.booking_id;
          } else if (record.order_id) {
            refType = 'order';
            refId = record.order_id;
          } else if (record.activity_id) {
            refType = 'activity';
            refId = record.activity_id;
          }

          const handlePrintFromTable = async () => {
            if (!refId) {
              message.error('No reservation references found for this invoice');
              return;
            }
            try {
              const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';
              const receiptUrl = refType === 'folio'
                ? `${baseUrl}/operations/billing/receipt/folio/${refId}`
                : `${baseUrl}/operations/billing/receipt/${refType}/${refId}`;
              const response = await fetch(receiptUrl, {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('admin_token') || ''}`,
                },
              });
              if (!response.ok) throw new Error('Failed to fetch receipt');
              const blob = await response.blob();
              const url = window.URL.createObjectURL(blob);
              const newWindow = window.open(url, '_blank');
              if (newWindow) newWindow.focus();
            } catch (error) {
              console.error(error);
              message.error('Failed to load receipt PDF');
            }
          };

          return (
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              {(() => {
                const isFolio = record.record_type === 'folio';
                const isClosed = record.status === 'closed';
                const balance = Number(record.total_amount || 0) - Number(record.paid_amount || 0);
                const hidePayment = isFolio ? (isClosed || balance <= 0) : record.status === 'paid';
                return !hidePayment && canSettle();
              })() && (
                <Button
                  size="small"
                  type="dashed"
                  onClick={() => {
                    setSelectedRefType(refType);
                    setSelectedRefId(refId);

                    unifiedBillingForm.setFieldsValue({
                      guest_name: record.guest_name,
                      guest_email: record.guest_email || '',
                      amount: Number(record.total_amount || 0) - Number(record.paid_amount || 0),
                      notes: `Settle Invoice ${record.invoice_number}`,
                    });
                  }}
                  style={{ borderColor: '#1890ff', color: '#1890ff', width: '100%' }}
                >
                  💳 Record Payment
                </Button>
              )}
              {refId && (
                <Button
                  size="small"
                  type="dashed"
                  onClick={handlePrintFromTable}
                  style={{ borderColor: '#52c41a', color: '#52c41a', width: '100%' }}
                >
                  🖨️ Print Receipt
                </Button>
              )}
            </Space>
          );
        },
      },
    ],
    [unifiedBillingForm]
  );



  const sportsCatalogColumns = useMemo(
    () => [
      { title: 'Activity', dataIndex: 'title', key: 'title' },
      {
        title: 'Image',
        dataIndex: 'image_url',
        key: 'image_url',
        render: (value: string | undefined) => (value ? 'Yes' : 'No'),
      },
      { title: 'Location', dataIndex: 'location', key: 'location' },
      { title: 'Capacity', dataIndex: 'max_capacity', key: 'max_capacity' },
      {
        title: 'Price',
        dataIndex: 'base_price',
        key: 'base_price',
        render: (value: number) => formatLkr(Number(value || 0)),
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_: any, record: any) => (
          canManageSports ? (
            <Space>
              <Button
                size="small"
                onClick={() => {
                  setEditingSportsCatalog(record);
                  sportsCatalogForm.setFieldsValue(record);
                }}
              >
                Edit
              </Button>
              <Button
                size="small"
                danger
                onClick={async () => {
                  try {
                    await adminOperationsService.deleteSportsCatalog(record.id);
                    message.success('Activity deleted');
                    if (editingSportsCatalog?.id === record.id) {
                      setEditingSportsCatalog(null);
                      sportsCatalogForm.resetFields();
                    }
                    fetchTabData('sports');
                  } catch (err) {
                    message.error(err instanceof Error ? err.message : 'Failed to delete sports activity');
                  }
                }}
              >
                Delete
              </Button>
            </Space>
          ) : null
        ),
      },
    ],
    [canManageSports, sportsCatalogForm, editingSportsCatalog, fetchTabData]
  );

  const sportsScheduleColumns = useMemo(
    () => [
      { title: 'Activity', dataIndex: 'activity_title', key: 'activity_title' },
      { title: 'Date', dataIndex: 'schedule_date', key: 'schedule_date', render: (val: string) => val ? dayjs(val).format('MMM D, YYYY') : 'N/A' },
      { title: 'Start', dataIndex: 'start_time', key: 'start_time', render: (val: string) => val ? dayjs(val, 'HH:mm:ss').format('h:mm A') : 'N/A' },
      { title: 'End', dataIndex: 'end_time', key: 'end_time', render: (val: string) => val ? dayjs(val, 'HH:mm:ss').format('h:mm A') : 'N/A' },
      { title: 'Trainer', dataIndex: 'trainer_name', key: 'trainer_name' },
      { title: 'Capacity', dataIndex: 'capacity', key: 'capacity' },
      { title: 'Status', dataIndex: 'status', key: 'status' },
    ],
    []
  );

  const reservationColumns = useMemo(
    () => [
      { title: 'Guest', dataIndex: 'full_name', key: 'full_name' },
      { title: 'Activity', dataIndex: 'activity_title', key: 'activity_title' },
      { title: 'Date', dataIndex: 'reservation_date', key: 'reservation_date', render: (val: string) => val ? dayjs(val).format('MMM D, YYYY') : 'N/A' },
      { title: 'Time', dataIndex: 'reservation_time', key: 'reservation_time', render: (val: string) => val ? dayjs(val, 'HH:mm:ss').format('h:mm A') : 'N/A' },
      { title: 'Guests', dataIndex: 'number_of_guests', key: 'number_of_guests' },
      { title: 'Status', dataIndex: 'status', key: 'status' },
    ],
    []
  );

  const notificationColumns = useMemo(
    () => [
      { title: 'Recipient', dataIndex: 'recipient_contact', key: 'recipient_contact' },
      { title: 'Channel', dataIndex: 'channel', key: 'channel' },
      { title: 'Status', dataIndex: 'status', key: 'status' },
      { title: 'Template', dataIndex: 'template_key', key: 'template_key' },
      { title: 'Created', dataIndex: 'created_at', key: 'created_at', render: (val: string) => val ? dayjs(val).format('MMM D, YYYY h:mm A') : 'N/A' },
    ],
    []
  );



  // Legacy billing handlers removed for unified billing structure

  const onSettleUnified = async (values: any) => {
    if (!selectedRefType || !selectedRefId) {
      message.error('Please select a billing target first');
      return;
    }

    setLoading(true);
    try {
      if (selectedRefType === 'folio') {
        await adminOperationsService.settleFolio({
          folio_id: selectedRefId,
          amount: Number(values.amount || 0),
          payment_method: values.payment_method,
          notes: values.notes,
        });
      } else {
        await adminOperationsService.settleUnified({
          type: selectedRefType,
          id: selectedRefId,
          payment_method: values.payment_method,
          amount: Number(values.amount || 0),
          notes: values.notes,
          guest_name: values.guest_name,
          guest_email: values.guest_email,
        });
      }

      message.success('Payment settled and recorded successfully');
      unifiedBillingForm.resetFields();
      setSelectedRefType(null);
      setSelectedRefId(null);

      await fetchTabData('billing');
    } catch (err: any) {
      message.error(err instanceof Error ? err.message : 'Failed to settle unified bill');
    } finally {
      setLoading(false);
    }
  };

  const onPrintUnifiedReceipt = async () => {
    if (!selectedRefType || !selectedRefId) {
      message.error('Please select a billing target first');
      return;
    }

    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';
      const receiptUrl = selectedRefType === 'folio'
        ? `${baseUrl}/operations/billing/receipt/folio/${selectedRefId}`
        : `${baseUrl}/operations/billing/receipt/${selectedRefType}/${selectedRefId}`;
      const response = await fetch(receiptUrl, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('admin_token') || ''}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch receipt');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const newWindow = window.open(url, '_blank');
      if (newWindow) newWindow.focus();
    } catch (error) {
      console.error(error);
      message.error('Failed to load receipt PDF');
    }
  };



  const onCreateSportsCatalog = async (values: any) => {
    try {
      if (editingSportsCatalog) {
        await adminOperationsService.updateSportsCatalog(editingSportsCatalog.id, values);
        message.success('Activity updated');
      } else {
        await adminOperationsService.createSportsCatalog(values);
        message.success('Activity added');
      }
      setEditingSportsCatalog(null);
      sportsCatalogForm.resetFields();
      fetchTabData('sports');
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Failed to create sports activity');
    }
  };

  const onUploadSportsCatalogImage = async (file: File) => {
    try {
      setLoading(true);
      const response = await adminOperationsService.uploadSportsCatalogImage(file);
      const imageUrl = String((response.data as { image_url?: string })?.image_url || '');

      if (!imageUrl) {
        message.error('Upload succeeded but no image URL was returned');
        return;
      }

      sportsCatalogForm.setFieldValue('image_url', imageUrl);
      message.success('Activity image uploaded successfully');
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Failed to upload activity image');
    } finally {
      setLoading(false);
    }
  };

  const onCreateSportsSchedule = async (values: any) => {
    try {
      await adminOperationsService.createSportsSchedule({
        ...values,
        schedule_date: values.schedule_date.format('YYYY-MM-DD'),
      });
      message.success('Schedule created');
      sportsScheduleForm.resetFields();
      fetchTabData('sports');
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Failed to create schedule');
    }
  };

  const onCreateNotification = async (values: any) => {
    try {
      await adminOperationsService.createNotification(values);
      message.success('Notification queued');
      notificationForm.resetFields();
      fetchTabData('notifications');
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Failed to create notification');
    }
  };



  const renderBilling = () => {
    let refOptions: Array<{ label: string; value: string }> = [];

    if (selectedRefType === 'booking') {
      refOptions = (billingReferences?.bookings || []).map((b: any) => ({
        label: `${b.confirmation_number || 'No Ref'} — ${b.guest_name} (${b.email})${b.room_count > 1 ? ` [${b.room_count} Rooms]` : ''} [Room Booking]`,
        value: b.id,
      }));
    } else if (selectedRefType === 'order') {
      refOptions = (billingReferences?.orders || []).map((o: any) => ({
        label: `${o.order_number} — ${o.order_type === 'room_service' ? 'Room Service' : `Table ${o.table_number || 'N/A'}`} — ${o.guest_name || 'Restaurant Guest'} (${formatLkr(Number(o.total_amount || 0))}) [${o.order_type === 'room_service' ? 'Room Service' : 'Restaurant POS'}]`,
        value: o.id,
      }));
    } else if (selectedRefType === 'activity') {
      refOptions = (billingReferences?.activities || []).map((a: any) => ({
        label: `${a.activity_title} — ${a.full_name} (${a.email}) [Adams Tour]`,
        value: a.id,
      }));
    } else if (selectedRefType === 'folio') {
      refOptions = (billingReferences?.folios || []).map((f: any) => ({
        label: `${f.guest_name} — ${f.confirmation_number || 'N/A'}${f.room_count > 1 ? ` (${f.room_count} Rooms)` : ''} — Balance: ${formatLkr(Number(f.balance || 0))} [Folio]`,
        value: f.folio_id || f.id,
      }));
    }

    return (
      <Row gutter={[16, 16]}>
        <Col xs={24} xl={10}>
          <Card title="Select Billing Target" style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                Select the service or department to begin billing:
              </Text>
              <Select
                placeholder="1. Select Billing Category"
                style={{ width: '100%', marginBottom: 12 }}
                onChange={(val) => {
                  setSelectedRefType(val);
                  setSelectedRefId(null);
                  unifiedBillingForm.resetFields();
                }}
                value={selectedRefType}
                options={[
                  { label: 'Guest Folio', value: 'folio' },
                  { label: 'Room Booking', value: 'booking' },
                  { label: 'Restaurant POS Order', value: 'order' },
                  { label: 'Adam\'s Tour / Sports Activity', value: 'activity' },
                ]}
              />

              {selectedRefType && (
                <Select
                  placeholder="2. Select Reference Number / Guest"
                  style={{ width: '100%' }}
                  showSearch
                  optionFilterProp="label"
                  value={selectedRefId}
                  onChange={async (val) => {
                    setSelectedRefId(val);
                    if (selectedRefType === 'booking') {
                      const item = billingReferences.bookings.find((b: any) => b.id === val);
                      if (item) {
                        unifiedBillingForm.setFieldsValue({
                          guest_name: item.guest_name,
                          guest_email: item.email,
                          amount: Number(item.total_price),
                          notes: `Unified Billing for Booking ${item.confirmation_number}`,
                        });
                      }
                    } else if (selectedRefType === 'order') {
                      const item = billingReferences.orders.find((o: any) => o.id === val);
                      if (item) {
                        unifiedBillingForm.setFieldsValue({
                          guest_name: item.guest_name || 'Restaurant Guest',
                          guest_email: '',
                          amount: Number(item.total_amount),
                          notes: `Unified Billing for POS Order ${item.order_number}`,
                        });
                      }
                    } else if (selectedRefType === 'activity') {
                      const item = billingReferences.activities.find((a: any) => a.id === val);
                      if (item) {
                        let estPrice = 0;
                        try {
                          const catItem = sportsCatalog.find((c: any) => c.title.toLowerCase() === item.activity_title.toLowerCase());
                          if (catItem) {
                            estPrice = Number(catItem.base_price) * Number(item.number_of_guests || 1);
                          }
                        } catch (e) { }

                        unifiedBillingForm.setFieldsValue({
                          guest_name: item.full_name,
                          guest_email: item.email,
                          amount: estPrice,
                          notes: `Unified Billing for Sports/Activity: ${item.activity_title}`,
                        });
                      }
                    } else if (selectedRefType === 'folio') {
                      const item = billingReferences.folios.find((f: any) => (f.folio_id || f.id) === val);
                      if (item) {
                        unifiedBillingForm.setFieldsValue({
                          guest_name: item.guest_name,
                          guest_email: item.guest_email || '',
                          amount: Number(item.balance || 0),
                          notes: `Folio Payment — ${item.confirmation_number || 'N/A'}${item.room_count > 1 ? ` (${item.room_count} Rooms)` : ''}`,
                        });
                      }
                    }
                  }}
                  options={refOptions}
                />
              )}
            </div>
          </Card>

          {selectedRefId && (
            <Card title="Billing & Settlement Detail">
              <Form layout="vertical" form={unifiedBillingForm} onFinish={onSettleUnified}>
                <Form.Item name="guest_name" label="Guest Name" rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
                <Form.Item name="guest_email" label="Guest Email">
                  <Input type="email" />
                </Form.Item>
                <Form.Item name="amount" label="Billing Amount (LKR)" rules={[{ required: true }]}>
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="payment_method" label="Payment Method" rules={[{ required: true }]}>
                  <Select options={PAYMENT_METHOD_OPTIONS} />
                </Form.Item>
                <Form.Item name="notes" label="Notes / Comments">
                  <Input.TextArea rows={3} />
                </Form.Item>

                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Button type="primary" htmlType="submit" size="large" style={{ minWidth: '150px' }}>
                    Settle Payment
                  </Button>
                  <Button type="default" size="large" onClick={onPrintUnifiedReceipt} style={{ borderColor: '#1890ff', color: '#1890ff' }}>
                    🖨️ Print Receipt
                  </Button>
                </Space>
              </Form>
            </Card>
          )}
        </Col>

        <Col xs={24} xl={14}>
          <Card title="Billing Overview" style={{ marginBottom: 16 }}>
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Statistic title="Total Billed" value={Number(billingOverview?.total_billed || 0)} prefix="LKR" />
              </Col>
              <Col span={8}>
                <Statistic title="Total Paid" value={Number(billingOverview?.total_paid || 0)} prefix="LKR" />
              </Col>
              <Col span={8}>
                <Statistic title="Outstanding" value={Number(billingOverview?.outstanding_amount || 0)} prefix="LKR" />
              </Col>
            </Row>
          </Card>
          <Card title="Recent Invoices & Transactions">
            <Table rowKey="id" dataSource={invoices} columns={invoiceColumns} pagination={{ pageSize: 6 }} scroll={{ x: 'max-content' }} />
          </Card>
        </Col>
      </Row>
    );
  };



  const renderSports = () => (
    <Row gutter={[16, 16]}>
      <Col xs={24} xl={10}>
        {canManageSports ? (
          <>
            <Card title="Add Adam's Tour Activity">
              <Form layout="vertical" form={sportsCatalogForm} onFinish={onCreateSportsCatalog}>
                <Form.Item name="title" label="Title" rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
                <Form.Item name="description" label="Description">
                  <Input.TextArea rows={3} />
                </Form.Item>
                <Form.Item name="image_url" label="Image URL">
                  <Input placeholder="https://... or /uploads/..." />
                </Form.Item>
                <Form.Item label="Upload Image">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={async (event) => {
                      const input = event.target as HTMLInputElement;
                      const file = input.files?.[0];
                      if (!file) return;
                      await onUploadSportsCatalogImage(file);
                      input.value = '';
                    }}
                  />
                </Form.Item>
                <Form.Item name="location" label="Location">
                  <Input />
                </Form.Item>
                <Form.Item name="max_capacity" label="Max Capacity" initialValue={10}>
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="base_price" label="Base Price" initialValue={0}>
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
                <Button htmlType="submit" block>
                  {editingSportsCatalog ? 'Update Activity' : 'Add Activity'}
                </Button>
                {editingSportsCatalog ? (
                  <Button
                    style={{ marginTop: 8 }}
                    block
                    onClick={() => {
                      setEditingSportsCatalog(null);
                      sportsCatalogForm.resetFields();
                    }}
                  >
                    Cancel Edit
                  </Button>
                ) : null}
              </Form>
            </Card>

            <Card title="Create Activity Schedule" style={{ marginTop: 16 }}>
              <Form layout="vertical" form={sportsScheduleForm} onFinish={onCreateSportsSchedule}>
                <Form.Item name="activity_id" label="Activity" rules={[{ required: true }]}>
                  <Select options={(sportsCatalog || []).map((item) => ({ value: item.id, label: item.title }))} />
                </Form.Item>
                <Form.Item name="schedule_date" label="Date" rules={[{ required: true }]}>
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="start_time" label="Start Time" rules={[{ required: true }]}>
                  <Input placeholder="09:00" />
                </Form.Item>
                <Form.Item name="end_time" label="End Time" rules={[{ required: true }]}>
                  <Input placeholder="11:00" />
                </Form.Item>
                <Form.Item name="trainer_name" label="Trainer">
                  <Input />
                </Form.Item>
                <Form.Item name="capacity" label="Capacity" rules={[{ required: true }]} initialValue={10}>
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
                <Button htmlType="submit" block>
                  Create Schedule
                </Button>
              </Form>
            </Card>
          </>
        ) : (
          <Alert
            type="info"
            showIcon
            message="Tour management is restricted"
            description="Only admin and manager roles can create tour activities and schedules."
          />
        )}
      </Col>

      <Col xs={24} xl={14}>
        <Card title="Adam's Tour Operations">
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <Text strong>Activity Catalog</Text>
              <Table rowKey="id" dataSource={sportsCatalog} columns={sportsCatalogColumns} size="small" pagination={false} scroll={{ x: 'max-content' }} />
            </div>
            <div>
              <Text strong>Schedules</Text>
              <Table rowKey="id" dataSource={sportsSchedules} columns={sportsScheduleColumns} size="small" pagination={{ pageSize: 5 }} scroll={{ x: 'max-content' }} />
            </div>
            <div>
              <Text strong>Reservations</Text>
              <Table rowKey="id" dataSource={sportsReservations} columns={reservationColumns} size="small" pagination={{ pageSize: 5 }} scroll={{ x: 'max-content' }} />
            </div>
          </Space>
        </Card>
      </Col>
    </Row>
  );

  const getListData = (value: dayjs.Dayjs) => {
    return calendarEntries.filter((event: any) => {
      const eventStart = dayjs(event.start_date);
      const eventEnd = dayjs(event.end_date);
      return (
        value.isSame(eventStart, 'day') ||
        value.isSame(eventEnd, 'day') ||
        (value.isAfter(eventStart, 'day') && value.isBefore(eventEnd, 'day'))
      );
    });
  };

  const dateCellRender = (value: dayjs.Dayjs) => {
    const dailyEvents = getListData(value);

    return (
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {dailyEvents.map((item: any) => {
          let badgeStatus: 'success' | 'processing' | 'warning' | 'default' | 'error' = 'default';
          if (item.source === 'room_booking') badgeStatus = 'success';
          else if (item.source === 'restaurant_reservation') badgeStatus = 'processing';
          else if (item.source === 'sports_reservation') badgeStatus = 'warning';

          return (
            <li key={item.id} style={{ marginBottom: 4 }}>
              <Badge
                status={badgeStatus}
                text={
                  <span style={{ fontSize: '12px' }}>
                    {item.title} ({item.source === 'room_booking' ? item.status : item.reference})
                  </span>
                }
              />
            </li>
          );
        })}
      </ul>
    );
  };

  const renderCalendar = () => (
    <Card title="Advanced Reservation Calendar Engine">
      <Row justify="space-between" style={{ marginBottom: 16 }}>
        <Col>
          <Text>Unified calendar for room, restaurant, and Adam's Tour reservations.</Text>
        </Col>
        <Col>
          <RangePicker value={dateRange} onChange={(value) => setDateRange(value as [dayjs.Dayjs, dayjs.Dayjs] | null)} />
        </Col>
      </Row>
      <div style={{ overflowX: 'auto' }}>
        <Calendar dateCellRender={dateCellRender} />
      </div>
    </Card>
  );

  const renderNotifications = () => (
    <Row gutter={[16, 16]}>
      <Col xs={24} xl={10}>
        <Card title="Trigger Notification Workflow">
          <Form layout="vertical" form={notificationForm} onFinish={onCreateNotification}>
            <Form.Item name="recipient_name" label="Recipient Name">
              <Input />
            </Form.Item>
            <Form.Item name="recipient_contact" label="Recipient Contact" rules={[{ required: true }]}>
              <Input placeholder="email@example.com or +1..." />
            </Form.Item>
            <Form.Item name="channel" label="Channel" rules={[{ required: true }]}>
              <Select
                options={[
                  { label: 'Email', value: 'email' },
                  { label: 'SMS', value: 'sms' },
                  { label: 'WhatsApp', value: 'whatsapp' },
                  { label: 'Push', value: 'push' },
                ]}
              />
            </Form.Item>
            <Form.Item name="send_now" label="Send Now" initialValue={false}>
              <Select
                options={[
                  { label: 'Queue (send later)', value: false },
                  { label: 'Send now', value: true },
                ]}
              />
            </Form.Item>
            <Form.Item name="subject" label="Subject (Email only)">
              <Input placeholder="Notification from Adam's Palm Resort" />
            </Form.Item>
            <Form.Item name="message" label="Message">
              <Input.TextArea rows={3} placeholder="Write the notification message here..." />
            </Form.Item>
            <Button type="primary" htmlType="submit" block>
              Send Notification
            </Button>
          </Form>
        </Card>
      </Col>
      <Col xs={24} xl={14}>
        <Card title="Notification Dispatches">
          <Table rowKey="id" dataSource={notifications} columns={notificationColumns} pagination={{ pageSize: 8 }} scroll={{ x: 'max-content' }} />
        </Card>
      </Col>
    </Row>
  );

  const getHeaderInfo = () => {
    switch (activeTab) {
      case 'billing':
        return {
          title: 'Billing Engine',
          subtitle: 'Unified resort checkout, invoice management, and manual payment receipting.',
        };
      case 'sports':
        return {
          title: "Adam's Tour Operations",
          subtitle: 'Manage activity schedules, capacities, and reservation slots.',
        };
      case 'calendar':
        return {
          title: 'Reservation Calendar',
          subtitle: 'Unified view of room bookings, dining table events, and tour schedules.',
        };
      case 'notifications':
        return {
          title: 'Notifications Center',
          subtitle: 'Broadcast alerts, email receipts, and transactional resort SMS notifications.',
        };
      default:
        return {
          title: 'Operations Suite',
          subtitle: 'Central billing, CRM, notifications, and calendar scheduling.',
        };
    }
  };

  const header = getHeaderInfo();

  const renderContent = () => {
    switch (activeTab) {
      case 'billing':
        return renderBilling();
      case 'sports':
        return renderSports();
      case 'calendar':
        return renderCalendar();
      case 'notifications':
        return renderNotifications();
      default:
        return null;
    }
  };

  return (
    <div>
      {error && (
        <Alert
          style={{ marginBottom: 16 }}
          type="error"
          showIcon
          message="Failed to load operations data"
          description={error}
        />
      )}

      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              {header.title}
            </Title>
            <Text type="secondary">
              {header.subtitle}
            </Text>
          </Col>
          <Col>
            <Button onClick={() => fetchTabData(activeTab)}>Refresh Data</Button>
          </Col>
        </Row>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <Spin size="large" />
          </div>
        ) : (
          renderContent()
        )}
      </Card>
    </div>
  );
};

export default OperationsSuite;
